<template>
  <div class="m-profile">
    <van-nav-bar title="个人资料" left-arrow @click-left="$router.back()" fixed placeholder />

    <!-- 骨架屏 -->
    <div v-if="loading" class="m-profile__skeleton">
      <div class="m-profile__skeleton-avatar-section">
        <div class="m-profile__skeleton-avatar"></div>
        <div class="m-profile__skeleton-hint"></div>
        <div class="m-profile__skeleton-code"></div>
      </div>
      <div class="m-profile__skeleton-title"></div>
      <div class="m-profile__skeleton-cell-group"><div class="m-profile__skeleton-cell"></div></div>
      <div class="m-profile__skeleton-title"></div>
      <div class="m-profile__skeleton-cell-group"><div class="m-profile__skeleton-cell"></div><div class="m-profile__skeleton-cell"></div></div>
      <div class="m-profile__skeleton-title"></div>
      <div class="m-profile__skeleton-cell-group"><div class="m-profile__skeleton-cell"></div><div class="m-profile__skeleton-cell"></div></div>
    </div>

    <template v-else>
    <!-- 头像区 -->
    <div class="m-profile__avatar-section">
      <!-- 第一块：个人资料 -->
      <div class="personal-info-avatar-wrap">
        <!-- 头像显示容器 - 包含头像和审核标签 -->
        <div 
          v-if="avatarDisplay.auditStatus !== 1 && avatarDisplay.auditStatus !== 3" 
          class="m-profile__avatar personal-info-avatar" 
          @click="triggerAvatarUpload"
        >
          <img :src="profile.avatar || ''" alt="avatar" />
          <span v-if="!profile.avatar">LOGO</span>
          <div class="m-profile__avatar-mask personal-info-avatar-mask">
            <span>修改</span>
          </div>
        </div>
        <!-- 审核中和驳回状态 - 不可点击 -->
        <div 
          v-if="(avatarDisplay.auditStatus === 1 || avatarDisplay.auditStatus === 3)"
          class="m-profile__avatar personal-info-avatar personal-info-avatar--readonly"
        >
          <img :src="avatarDisplay.src" alt="avatar" />
          <span v-if="!avatarDisplay.src">LOGO</span>
          <!-- 审核中/驳回提示（在头像顶部） -->
          <div v-if="avatarDisplay.auditStatus === 1" class="personal-avatar-audit-tip personal-avatar-tag-pending">审核中</div>
          <!-- 驳回提示（在头像顶部） -->
          <div v-if="avatarDisplay.auditStatus === 3" class="personal-avatar-audit-tip personal-avatar-tag-rejected">驳回</div>
        </div>
      </div>
      <input ref="avatarInput" type="file" accept="image/*" style="display:none" @change="onAvatarChange" />
      <div class="m-profile__user-code">用户编号：{{ userCode || '--' }}</div>
    </div>

    <!-- 基本信息 -->
    <div class="m-profile__section-title">基本信息</div>
    <van-cell-group class="m-profile__group">
      <van-cell title="昵称" :value="profile.nickname" is-link @click="openNicknameDialog" />
    </van-cell-group>

    <!-- 登录信息 -->
    <div class="m-profile__section-title">登录信息</div>
    <van-cell-group class="m-profile__group">
      <van-cell title="绑定手机" :value="loginInfo.mobile" is-link @click="openMobileDialog" />
      <van-cell title="登录密码" value="********" is-link @click="openPasswordDialog" />
    </van-cell-group>

    <!-- 实名认证 -->
    <div class="m-profile__section-title">实名认证</div>
    <van-cell-group class="m-profile__group">
      <van-cell title="认证状态">
        <template #right-icon>
          <van-tag :type="authTagType" size="medium">{{ authStatusText }}</van-tag>
        </template>
      </van-cell>
      <div v-if="authStatus === 3 && auditReason" class="m-profile__auth-reason">
        失败原因：{{ auditReason }}
      </div>
      <div v-if="authInfo.name" class="m-profile__auth-detail">
        <van-cell title="姓名" :value="authInfo.name" />
        <van-cell title="身份证号" :value="authInfo.idCard" />
        <van-cell title="出生日期" :value="authInfo.birthday" />
        <van-cell title="性别" :value="authInfo.gender" />
        <van-cell title="民族" :value="authInfo.nation" />
        <van-cell title="证件有效期" :value="authInfo.validity" />
        <van-cell title="签发单位" :value="authInfo.issuer" />
      </div>
      <div v-if="authStatus === 0 || authStatus === 3" class="m-profile__auth-action">
        <van-button type="primary" size="small" round @click="openAuthDialog">修改认证信息</van-button>
      </div>
    </van-cell-group>
    </template>

    <!-- 修改昵称弹窗 -->
    <van-dialog
      v-model="showNicknameDialog"
      title="修改昵称"
      show-cancel-button
      @confirm="saveNickname"
    >
      <van-field v-model="form.nickname" placeholder="请输入昵称" @input="handleNicknameInput" class="m-profile__dialog-field" />
    </van-dialog>

    <!-- 修改手机号弹窗 -->
    <van-dialog
      v-model="showMobileDialog"
      title="修改手机号码"
      show-cancel-button
      @cancel="showMobileDialog = false"
       :before-close="mobileBeforeClose"
    >
      <div class="m-profile__dialog-content">
        <div class="m-profile__code-row" style="margin-bottom: 12px;">
          <span style="color: #666; font-size: 14px;">登录手机号：</span>
          <span style="color: #333; font-size: 14px;">{{ loginInfo.mobile }}</span>
        </div>
        <van-field v-model="mobileForm.newMobile" placeholder="请输入新手机号" type="tel" maxlength="11" left-icon="phone-o" />
        <van-field v-model="mobileForm.confirmNewMobile" placeholder="请再次输入新手机号" type="tel" maxlength="11" left-icon="phone-o" />
        <div class="m-profile__code-row">
          <van-field v-model="mobileForm.code" placeholder="请输入图形验证码" maxlength="6" left-icon="shield-o" class="m-profile__code-field" />
          <div style="width: 80px; height: 36px; flex-shrink: 0; border-radius: 4px; overflow: hidden; background: #f5f5f5; cursor: pointer;" @click="refreshBindingCaptcha">
            <img v-if="bindingCaptchaUrl" :src="bindingCaptchaUrl" alt="图形验证码" style="width: 100%; height: 100%; object-fit: cover;" />
            <span v-else style="font-size: 11px; color: #666; display: flex; align-items: center; justify-content: center; height: 100%;">点击获取</span>
          </div>
        </div>
        <div class="m-profile__code-row">
          <van-field v-model="mobileForm.smsCode" placeholder="请输入短信验证码" type="digit" maxlength="6" left-icon="envelop-o" class="m-profile__code-field" />
          <van-button plain type="primary" size="small" :disabled="countdown > 0" @click="handleSendMobileCode" class="m-profile__code-btn">
            {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
          </van-button>
        </div>
      </div>
    </van-dialog>

    <!-- 修改密码弹窗 -->
    <van-dialog
      v-model="showPasswordDialog"
      title="修改密码"
      show-cancel-button
      :before-close="passwordBeforeClose"
      @cancel="showPasswordDialog = false"
    >
      <div class="m-profile__dialog-content">
        <div class="m-profile__code-row">
          <van-field v-model="passwordForm.captchaInput" placeholder="请输入图形验证码" maxlength="6" left-icon="shield-o" class="m-profile__code-field" />
          <div style="width: 80px; height: 36px; flex-shrink: 0; border-radius: 4px; overflow: hidden; background: #f5f5f5; cursor: pointer;" @click="refreshPasswordCaptcha">
            <img v-if="passwordCaptchaUrl" :src="passwordCaptchaUrl" alt="图形验证码" style="width: 100%; height: 100%; object-fit: cover;" />
            <span v-else style="font-size: 11px; color: #666; display: flex; align-items: center; justify-content: center; height: 100%;">点击获取</span>
          </div>
        </div>
        <div class="m-profile__code-row">
          <van-field v-model="passwordForm.smsCode" placeholder="请输入短信验证码" type="digit" maxlength="6" left-icon="envelop-o" class="m-profile__code-field" />
          <van-button plain type="primary" size="small" :disabled="passwordCountdown > 0" @click="handleSendPasswordCode" class="m-profile__code-btn">
            {{ passwordCountdown > 0 ? `${passwordCountdown}s` : '获取验证码' }}
          </van-button>
        </div>
        <van-field v-model="passwordForm.newPassword" placeholder="请输入新密码" type="password" left-icon="lock" />
        <van-field v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码" type="password" left-icon="lock" />
        <div class="m-profile__pwd-hint">密码长度必须大于8位，并且是复杂密码（包含大小写字母、数字、特殊符号中的至少 2 种）</div>
      </div>
    </van-dialog>

    <!-- 实名认证弹窗 -->
    <van-popup v-model="showAuthDialog" position="bottom" round :style="{ maxHeight: '85%' }" closeable>
      <div class="m-profile__auth-popup">
        <div class="m-profile__auth-popup-title">实名认证</div>
        <div class="m-profile__auth-upload-row">
          <div class="m-profile__auth-upload-box" @click="triggerIdCardUpload('front')">
            <img v-if="form.idCardFront" :src="form.idCardFront" alt="正面" />
            <div v-else class="m-profile__auth-upload-placeholder">
              <van-icon name="photograph" size="24" color="#ccc" />
              <span>身份证正面</span>
            </div>
          </div>
          <div class="m-profile__auth-upload-box" @click="triggerIdCardUpload('back')">
            <img v-if="form.idCardBack" :src="form.idCardBack" alt="背面" />
            <div v-else class="m-profile__auth-upload-placeholder">
              <van-icon name="photograph" size="24" color="#ccc" />
              <span>身份证国徽面</span>
            </div>
          </div>
        </div>
        <input ref="idCardFrontInput" type="file" accept="image/*" style="display:none" @change="onIdCardChange($event, 'front')" />
        <input ref="idCardBackInput" type="file" accept="image/*" style="display:none" @change="onIdCardChange($event, 'back')" />
        <van-field v-model="form.name" label="姓名" placeholder="请输入姓名" @input="handleNameInput" />
        <van-field v-model="form.idCard" label="身份证号" placeholder="请输入身份证号" @input="handleIdCardInput" />
        <van-field v-model="form.birthday" label="出生日期" placeholder="请选择出生日期" readonly clickable @click="openBirthdayPicker" />
        <div class="m-profile__radio-row">
          <span class="m-profile__radio-label">性别</span>
          <van-radio-group v-model="form.gender" direction="horizontal">
            <van-radio name="男">男</van-radio>
            <van-radio name="女">女</van-radio>
          </van-radio-group>
        </div>
        <van-field v-model="form.nation" label="民族" placeholder="请输入民族" @input="handleNationInput" />
        <div class="m-profile__validity-row">
          <van-field v-model="form.validityStart" label="有效期开始" placeholder="选择日期" readonly clickable @click="openValidityStartPicker" />
          <div class="m-profile__validity-end-row">
            <van-field v-model="form.validityEnd" label="有效期结束" placeholder="选择日期" readonly clickable :disabled="isLongTerm" @click="openValidityEndPicker" />
            <div class="m-profile__longterm" @click="isLongTerm = !isLongTerm">
              <van-checkbox v-model="isLongTerm" shape="square" icon-size="14" />
              <span>长期</span>
            </div>
          </div>
        </div>
        <van-field v-model="form.issuer" label="签发单位" placeholder="请输入签发单位" @input="handleIssuerInput" />
        <div style="padding: 16px">
          <van-button type="primary" block round @click="saveAuthInfo">提交认证</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 日期选择器 -->
    <van-popup v-model="showDatePicker" position="bottom" round>
      <van-datetime-picker
        v-model="currentPickerDate"
        type="date"
        :title="datePickerTitle"
        :min-date="minDate"
        :max-date="maxDate"
        @confirm="onDateConfirm"
        @cancel="showDatePicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import mobileViewport from './mixins/viewport'
import { Toast } from 'vant'
import {
  getUserInfo,
  changeAvatar,
  changeNickname,
  changePhone,
  changePassword,
  submitIdInfo
} from '../../api/member'
import request from '../../api/request'
import CryptoJS from 'crypto-js'

export default {
  mixins: [mobileViewport],
  name: 'MobilePersonalInfo',
  data() {
    return {
      profile: { avatar: '', nickname: '' },
      // 新头像审核状态相关字段
      newAvatar: '',
      newAvatarAuditStatus: 0,
      userCode: '',
      loginInfo: { mobile: '', password: '********' },
      authInfo: {
        idCardFront: '', idCardBack: '', name: '', idCard: '',
        birthday: '', gender: '', nation: '', validity: '',
        issuer: ''
      },
      authStatus: 0,
      auditReason: '',
      showNicknameDialog: false,
      showMobileDialog: false,
      showPasswordDialog: false,
      showAuthDialog: false,
      countdown: 0,
      timer: null,
      form: {
        nickname: '', idCardFront: '', idCardBack: '', name: '',
        idCard: '', birthday: '', gender: '', nation: '',
        validityStart: '', validityEnd: '', issuer: ''
      },
      mobileForm: {
        newMobile: '',
        confirmNewMobile: '',
        code: '',        // 图形验证码输入框
        smsCode: '',     // 短信验证码输入框
        uuid: ''         // 图形验证码 UUID
      },
      // 图形验证码相关
      bindingCaptchaUrl: '',
      passwordForm: {
        captchaInput: '',   // 图形验证码输入框
        smsCode: '',        // 短信验证码输入框
        newPassword: '',
        confirmPassword: ''
      },
      // 密码图形验证码相关
      passwordCountdown: 0,
      passwordTimer: null,
      passwordCaptchaUrl: '',
      passwordCaptchaUuid: '',
      // 日期选择
      showDatePicker: false,
      datePickerType: 'birthday',
      currentPickerDate: new Date(),
      minDate: new Date(1900, 0, 1),
      maxDate: new Date(2100, 11, 31),
      isLongTerm: false,
      loading: true
    }
  },
  computed: {
    authStatusText() {
      const map = { 0: '未认证', 1: '待审核', 2: '已认证', 3: '认证失败' }
      return map[this.authStatus] || '未知'
    },
    authTagType() {
      const map = { 0: 'default', 1: 'warning', 2: 'success', 3: 'danger' }
      return map[this.authStatus] || 'default'
    },
    datePickerTitle() {
      const map = { birthday: '选择出生日期', start: '选择有效期开始', end: '选择有效期结束' }
      return map[this.datePickerType] || '选择日期'
    },
    /**
     * 头像显示逻辑
     * - newAvatarAuditStatus 为 1 时显示 newAvatar（审核中）
     * - newAvatarAuditStatus 为 3 时显示 newAvatar（驳回）
     * - 其他情况显示 avatar
     */
    avatarDisplay() {
      const auditStatus = this.newAvatarAuditStatus
      let src = ''
      
      if (auditStatus === 1 || auditStatus === 3) {
        // 审核中和驳回都显示 newAvatar
        src = this.newAvatar || ''
      } else {
        // 其他情况显示原头像
        src = this.profile.avatar || ''
      }
      
      return {
        src,
        auditStatus
      }
    }
  },
  created() {
    this.getUserInfo()
  },
  beforeDestroy() {
    this.clearTimer()
    if (this.passwordTimer) {
      clearInterval(this.passwordTimer)
      this.passwordTimer = null
    }
  },
  methods: {
    async getUserInfo() {
      try {
        const res = await getUserInfo()
        const data = res.data || {}
        this.userCode = data.userCode || ''
        this.profile = { avatar: data.avatar || '', nickname: data.nickname || '' }
        // 新头像审核状态相关字段
        this.newAvatar = data.newAvatar || ''
        this.newAvatarAuditStatus = data.newAvatarAuditStatus || 0
        this.loginInfo = { mobile: data.mobile || '', password: '********' }
        this.authStatus = data.realNameStatus || 0
        this.auditReason = data.auditReason || ''
        const validityStart = this.formatDate(data.idCardValidityStart)
        let validityEnd = this.formatDate(data.idCardValidityEnd)
        if (validityEnd === '2199-01-01') validityEnd = '长期'
        this.authInfo = {
          idCardFront: data.idCardFrontImage || '',
          idCardBack: data.idCardBackImage || '',
          name: data.realName || '',
          idCard: data.idCardNo || '',
          birthday: this.formatDate(data.birthday),
          gender: this.formatGender(data.gender),
          nation: data.ethnicity || '',
          validity: validityStart && validityEnd ? `${validityStart} 至 ${validityEnd}` : '',
          issuer: data.idCardIssueAuthority || ''
        }
      } catch (err) {
        Toast(err.message || '获取用户信息失败')
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      if (isNaN(d.getTime())) return String(date)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    formatGender(gender) {
      const map = { 0: '未知', 1: '男', 2: '女' }
      return map[gender] || '未知'
    },
    // 头像上传
    triggerAvatarUpload() { this.$refs.avatarInput.click() },
    async onAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        await changeAvatar(url)
        // 上传后重新获取用户信息，更新头像审核状态
        await this.getUserInfo()
        Toast('头像上传成功')
      } catch (err) {
        Toast(err.message || '头像上传失败')
      }
    },
    async uploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const res = await request.post('/api/common/uploadFile/picFile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return res.data
      } catch (err) {
        console.error('uploadFile error:', err)
        throw err
      }
    },
    updateLocalUserInfo() {
      try {
        const userInfoStr = localStorage.getItem('userInfo')
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {}
        userInfo.nickname = this.profile.nickname
        userInfo.avatar = this.profile.avatar
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
      } catch (e) { /* ignore */ }
    },
    // 昵称
    openNicknameDialog() {
      this.form.nickname = this.profile.nickname
      this.showNicknameDialog = true
    },
     /** 昵称输入长度限制（英文数字占 1 字符，汉字占 2 字符） */
    handleNicknameInput() {
      // 移除特殊字符
      const reg = /[^一-\u9fa5a-zA-Z0-9]/g
      this.form.nickname = this.form.nickname.replace(reg, '')
      // 长度限制
      this.form.nickname = this.limitCharLength(this.form.nickname, 20)
    },
    /** 按字符权重截断字符串（英文数字占 1，汉字占 2） */
    limitCharLength(str, maxLen) {
      if (!str) return str
      let len = 0
      let i = 0
      for (; i < str.length; i++) {
        const charLen = str.charCodeAt(i) > 127 ? 2 : 1
        if (len + charLen > maxLen) break
        len += charLen
      }
      return str.substring(0, i)
    },
    async saveNickname() {
      const nickname = (this.form.nickname || '').trim()
      if (!nickname) { Toast('昵称不能为空'); return }
      try {
        await changeNickname(nickname)
        this.profile.nickname = nickname
        this.updateLocalUserInfo()
        Toast('昵称修改成功')
      } catch (err) {
        Toast(err.message || '昵称修改失败')
      }
    },
    // 手机号
    openMobileDialog() {
      this.mobileForm = {
        newMobile: '',
        confirmNewMobile: '',
        code: '',
        smsCode: '',
        uuid: ''
      }
      this.showMobileDialog = true
      this.loadBindingCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    async mobileBeforeClose(action, done) {
      if (action !== 'confirm') { done(); return }
      const newMobile = (this.mobileForm.newMobile || '').trim()
      const confirmNewMobile = (this.mobileForm.confirmNewMobile || '').trim()
      const smsCode = (this.mobileForm.smsCode || '').trim()
      
      if (!newMobile) {
        Toast('请输入新手机号');done(false); return
      }
      if (!/^1[3-9]\d{9}$/.test(newMobile)) {
        Toast('请输入正确的手机号');done(false); return
      }
      if (!confirmNewMobile) {
        Toast('请确认新手机号');done(false); return
      }
      if (newMobile !== confirmNewMobile) {
        Toast('两次输入的新手机号不一致');done(false); return
      }
      if (!smsCode) {
        Toast('请输入短信验证码');done(false); return
      }
      try {
        await changePhone({ code: smsCode, newMobile, password: '' })
        this.loginInfo.mobile = newMobile
        Toast('手机号修改成功')
        this.showMobileDialog = false
      } catch (err) {
        Toast(err.message || '手机号修改失败')
        done(false);
      }
    },
    // 密码
    openPasswordDialog() {
      this.passwordForm = {
        captchaInput: '',
        smsCode: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordCountdown = 0
      this.showPasswordDialog = true
      this.loadPasswordCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    /**
     * 密码弹窗关闭前验证
     * @param {string} action - 'confirm' 或 'cancel'
     * @param {Function} done - 调用 done() 关闭弹窗，done(false) 重置按钮不关闭
     */
    async passwordBeforeClose(action, done) {
      if (action !== 'confirm') { done(); return }
      
      const { smsCode, newPassword, confirmPassword } = this.passwordForm
      if (!smsCode) { Toast('请输入短信验证码'); done(false); return }
      if (!newPassword) { Toast('请输入新密码'); done(false); return }
      
      // 密码验证：长度 8-20 位，且至少包含两种字符类型 (大小写字母、数字、特殊符号)
      if (newPassword.length < 8 || newPassword.length > 20) {
        Toast('密码长度必须在 8-20 个字符之间')
        done(false); return
      }
      
      // 检查密码复杂度
      const hasUpper = /[A-Z]/.test(newPassword)
      const hasLower = /[a-z]/.test(newPassword)
      const hasDigit = /\d/.test(newPassword)
      const hasSpecial = /[!@#$%^&*()_+=][{};':"\\|,.<>?~`-]/.test(newPassword)
      
      const typeCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length
      
      if (typeCount < 2) {
        Toast('密码必须包含至少两种字符类型 (大小写字母、数字、特殊符号)')
        done(false); return
      }
      
      if (newPassword !== confirmPassword) {
        Toast('两次密码输入不一致')
        done(false); return
      }
      
      try {
        // AES 加密密码（与登录页面保持一致）
        const encryptedPassword = this.encryptPassword(newPassword)
        await changePassword({
          mobile: this.loginInfo.mobile, smsCode: smsCode, password: encryptedPassword
        })
        Toast('密码修改成功')
        done()  // 成功后关闭弹窗
      } catch (err) {
        Toast(err.message || '密码修改失败')
        done(false)  // 失败时重置按钮，保持弹窗打开
      }
    },
    /**
     * AES-ECB-PKCS7Padding 加密方法
     * @param {string} text - 明文密码
     * @returns {string} Base64 编码后的密文
     */
    encryptPassword(text) {
      const SECRET_KEY = CryptoJS.enc.Utf8.parse('zp_base_aes_2026')
      const wordArray = CryptoJS.enc.Utf8.parse(text)
      const encrypted = CryptoJS.AES.encrypt(wordArray, SECRET_KEY, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      })
      return encrypted.toString()
    },
    // 发送验证码 (修改手机号时使用)
    async handleSendMobileCode() {
      if (this.countdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.mobileForm.code) {
        Toast('请输入图形验证码'); return
      }
      try {
        await request.post('/api/member/getMessage', { mobile: null, areaCode: '86',
          captchaCode: this.mobileForm.code,
          uuid: this.mobileForm.uuid
        })
        this.startCountdown()
        Toast('验证码已发送')
        this.loadBindingCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    // 加载图形验证码
    loadBindingCaptcha() {
      request.get('/api/captchaImage').then(res => {
        if (res.code === 200) {
          this.bindingCaptchaUrl = `data:image/png;base64,${res.img}`
          this.mobileForm.uuid = res.uuid || ''
        }
      }).catch(err => {
        console.error('获取验证码失败:', err)
      })
    },
    // 刷新图形验证码
    refreshBindingCaptcha() {
      this.loadBindingCaptcha()
    },
    // 发送密码验证码
    async handleSendPasswordCode() {
      if (this.passwordCountdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.passwordForm.captchaInput) {
        Toast('请输入图形验证码'); return
      }
      try {
        await request.post('/api/member/getMessage', { mobile: null, areaCode: '86',
          captchaCode: this.passwordForm.captchaInput,
          uuid: this.passwordCaptchaUuid
        })
        this.startPasswordCountdown()
        Toast('验证码已发送')
        this.loadPasswordCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    // 加载密码图形验证码
    loadPasswordCaptcha() {
      request.get('/api/captchaImage').then(res => {
        if (res.code === 200) {
          this.passwordCaptchaUrl = `data:image/png;base64,${res.img}`
          this.passwordCaptchaUuid = res.uuid || ''
        }
      }).catch(err => {
        console.error('获取密码验证码失败:', err)
      })
    },
    // 刷新密码图形验证码
    refreshPasswordCaptcha() {
      this.loadPasswordCaptcha()
    },
    startPasswordCountdown() {
      this.passwordCountdown = 60
      this.passwordTimer = setInterval(() => {
        this.passwordCountdown--
        if (this.passwordCountdown <= 0) { clearInterval(this.passwordTimer); this.passwordTimer = null }
      }, 1000)
    },
    startCountdown() {
      this.countdown = 60
      this.timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) { clearInterval(this.timer); this.timer = null }
      }, 1000)
    },
    clearTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    },
    // 日期选择
    openBirthdayPicker() {
      this.datePickerType = 'birthday'
      this.maxDate = new Date()
      this.currentPickerDate = this.parseDate(this.form.birthday) || new Date()
      this.showDatePicker = true
    },
    openValidityStartPicker() {
      this.datePickerType = 'start'
      this.maxDate = new Date(2100, 11, 31)
      this.currentPickerDate = this.parseDate(this.form.validityStart) || new Date()
      this.showDatePicker = true
    },
    openValidityEndPicker() {
      if (this.isLongTerm) return
      this.datePickerType = 'end'
      this.maxDate = new Date(2100, 11, 31)
      this.currentPickerDate = this.parseDate(this.form.validityEnd) || new Date()
      this.showDatePicker = true
    },
    parseDate(dateStr) {
      if (!dateStr || dateStr === '长期') return null
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? null : d
    },
    onDateConfirm(value) {
      const formatted = this.formatDate(value)
      if (this.datePickerType === 'birthday') {
        this.form.birthday = formatted
      } else if (this.datePickerType === 'start') {
        this.form.validityStart = formatted
      } else if (this.datePickerType === 'end') {
        this.form.validityEnd = formatted
        this.isLongTerm = false
      }
      this.showDatePicker = false
    },
    // 实名认证
    openAuthDialog() {
      const validityParts = String(this.authInfo.validity || '').split('至').map(s => s.trim())
      const validityStart = validityParts[0] || ''
      let validityEnd = validityParts[1] || ''
      const isLongTerm = validityEnd === '长期' || validityEnd === '2199-01-01'
      this.isLongTerm = isLongTerm
      this.form = {
        ...this.authInfo,
        validityStart,
        validityEnd: isLongTerm ? '长期' : validityEnd
      }
      this.showAuthDialog = true
    },
    /** 姓名字段输入处理：仅允许 2-20 个汉字或"·" */
    handleNameInput() {
      let cleaned = this.form.name.replace(/[^\u4e00-\u9fa5·]/g, '')
      if (cleaned.length > 20) {
        cleaned = cleaned.substring(0, 20)
      }
      this.form.name = cleaned
    },
    /** 身份证号码输入处理：仅允许 17 位数字 + 第 18 位（数字或 x/X） */
    handleIdCardInput() {
      let cleaned = this.form.idCard.replace(/[^0-9axX]/g, '')
      if (cleaned.length > 18) {
        cleaned = cleaned.substring(0, 18)
      }
      if (cleaned.length === 18) {
        const lastChar = cleaned.charAt(17)
        if (lastChar === 'x' || lastChar === 'X') {
          cleaned = cleaned.substring(0, 17) + 'x'
        }
      }
      this.form.idCard = cleaned
    },
    /** 民族输入处理：仅允许汉字，最多 20 个字符 */
    handleNationInput() {
      let cleaned = this.form.nation.replace(/[^\u4e00-\u9fa5]/g, '')
      if (cleaned.length > 20) {
        cleaned = cleaned.substring(0, 20)
      }
      this.form.nation = cleaned
    },
    /** 签发单位输入处理：仅中文输入，长度不超过 30 字节 */
    handleIssuerInput() {
      let cleaned = this.form.issuer.replace(/[^\u4e00-\u9fa5·]/g, '')
      let byteLen = 0
      let validLen = 0
      for (let i = 0; i < cleaned.length; i++) {
        const charByteLen = 2
        if (byteLen + charByteLen > 30) break
        byteLen += charByteLen
        validLen++
      }
      if (validLen < cleaned.length) {
        cleaned = cleaned.substring(0, validLen)
      }
      this.form.issuer = cleaned
    },
    async saveAuthInfo() {
      // 验证姓名
      if (!this.form.name || !String(this.form.name).trim()) {
        Toast('请输入姓名'); return
      }
      const nameReg = /^[\u4e00-\u9fa5·]{2,20}$/
      if (!nameReg.test(this.form.name)) {
        Toast('姓名应为 2-20 个汉字或"·"'); return
      }
      
      // 验证身份证号
      if (!this.form.idCard || !String(this.form.idCard).trim()) {
        Toast('请输入身份证号'); return
      }
      if (this.form.idCard.length !== 18) {
        Toast('身份证号应为 18 位'); return
      }
      const idCardReg = /^[0-9]{17}[0-9xX]$/
      if (!idCardReg.test(this.form.idCard)) {
        Toast('身份证号格式不正确'); return
      }
      
      // 验证出生日期
      if (!this.form.birthday || !String(this.form.birthday).trim()) {
        Toast('请选择出生日期'); return
      }
      
      // 验证性别
      if (!this.form.gender || !['男', '女'].includes(this.form.gender)) {
        Toast('请选择性别'); return
      }
      
      // 验证民族
      if (!this.form.nation || !String(this.form.nation).trim()) {
        Toast('请输入民族'); return
      }
      
      // 验证有效期开始
      if (!this.form.validityStart || !String(this.form.validityStart).trim()) {
        Toast('请选择证件有效期开始日期'); return
      }
      
      // 验证有效期结束
      if (!this.isLongTerm && !this.form.validityEnd) {
        Toast('请选择证件有效期结束日期或选择长期'); return
      }
      
      // 验证签发单位
      if (!this.form.issuer || !String(this.form.issuer).trim()) {
        Toast('请输入签发单位'); return
      }
      
      const longTermDate = '2199-01-01'
      const genderMap = { '未知': 0, '男': 1, '女': 2 }
      const params = {
        realName: this.form.name,
        idCardNo: this.form.idCard,
        idCardFrontImage: this.form.idCardFront,
        idCardBackImage: this.form.idCardBack,
        ethnicity: this.form.nation,
        idCardIssueAuthority: this.form.issuer,
        idCardValidityStart: this.form.validityStart,
        idCardValidityEnd: this.isLongTerm ? longTermDate : this.form.validityEnd,
        gender: genderMap[this.form.gender] !== undefined ? genderMap[this.form.gender] : 0,
        birthday: this.form.birthday
      }
      try {
        await submitIdInfo(params)
        const displayEnd = this.isLongTerm ? '长期' : this.form.validityEnd
        this.authInfo = {
          ...this.form,
          validity: `${this.form.validityStart} 至 ${displayEnd}`
        }
        this.authStatus = 1
        this.showAuthDialog = false
        Toast('实名认证信息提交成功')
      } catch (err) {
        Toast(err.message || '实名认证信息提交失败')
      }
    },
    // 身份证上传
    triggerIdCardUpload(type) {
      const ref = type === 'front' ? 'idCardFrontInput' : 'idCardBackInput'
      this.$refs[ref].click()
    },
    async onIdCardChange(e, type) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        const key = type === 'front' ? 'idCardFront' : 'idCardBack'
        this.form[key] = url
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    }
  }
}
</script>

<style scoped>
.m-profile__skeleton {
  min-height: 60vh;
}
.m-profile__skeleton-avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 20px;
  background: #fff;
  margin-bottom: 12px;
}
.m-profile__skeleton-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-profile-shimmer 1.4s ease infinite;
}
.m-profile__skeleton-hint {
  width: 80px;
  height: 14px;
  border-radius: 4px;
  margin-top: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-profile-shimmer 1.4s ease infinite;
}
.m-profile__skeleton-code {
  width: 120px;
  height: 14px;
  border-radius: 4px;
  margin-top: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-profile-shimmer 1.4s ease infinite;
}
.m-profile__skeleton-title {
  width: 80px;
  height: 16px;
  border-radius: 4px;
  margin: 16px 16px 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-profile-shimmer 1.4s ease infinite;
}
.m-profile__skeleton-cell-group {
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.m-profile__skeleton-cell {
  height: 52px;
  border-bottom: 1px solid #f5f5f5;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-profile-shimmer 1.4s ease infinite;
}
@keyframes m-profile-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.m-profile {
  min-height: 100vh;
  background: #F5F7FA;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding-bottom: 20px;
}

/* 头像区 */
.m-profile__avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 20px;
  background: #fff;
  margin-bottom: 12px;
}

.m-profile__avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

/* 只读模式（审核中）- 无点击效果 */
.personal-info-avatar--readonly {
  cursor: default;
}

.m-profile__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 原头像遮罩样式 */
.m-profile__avatar-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.m-profile__avatar:hover .m-profile__avatar-mask {
  opacity: 1;
}

.m-profile__avatar-mask span {
  color: #FFFFFF;
  font-size: 12px;
}

.m-profile__avatar-hint {
  font-size: 12px;
  color: #0091EA;
  margin-top: 8px;
}

/* 头像审核状态提示 - 相对于头像容器定位 */
.personal-avatar-audit-tip {
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* 审核中 - 蓝色 */
.personal-avatar-tag-pending {
  background: rgba(225, 243, 248, 0.9);
  color: #23B4DC;
}

/* 驳回 - 红色 */
.personal-avatar-tag-rejected {
  background: rgba(255, 241, 240, 0.9);
  color: #FF4D4F;
}

.m-profile__avatar-section {
  position: relative;
}

.m-profile__user-code {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* Cell 组 */
.m-profile__section-title {
  font-size: 13px;
  color: #999;
  padding: 16px 16px 8px;
}

.m-profile__group {
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;
}

/* 认证失败原因 */
.m-profile__auth-reason {
  margin: 8px 16px;
  padding: 8px 12px;
  background: #FFF1F0;
  border-radius: 8px;
  font-size: 13px;
  color: #FF4D4F;
}

.m-profile__auth-detail {
  border-top: 1px solid #ebedf0;
}

.m-profile__auth-action {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* 弹窗内容 */
.m-profile__dialog-field {
  padding: 16px;
}

.m-profile__dialog-content {
  padding: 12px 16px;
}

.m-profile__dialog-content .van-field {
  margin-bottom: 8px;
  border-radius: 8px;
  background: #F8FAFC;
}

.m-profile__code-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.m-profile__code-field {
  flex: 1;
}

.m-profile__code-btn {
  flex-shrink: 0;
  height: 44px;
  border-radius: 8px;
  white-space: nowrap;
}

/* 密码提示 */
.m-profile__pwd-hint {
  font-size: 12px;
  color: #999;
  margin: -2px 4px 8px;
  line-height: 1.5;
}

/* 实名认证弹窗 */
.m-profile__auth-popup {
  padding-bottom: env(safe-area-inset-bottom);
}

.m-profile__auth-popup-title {
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 0;
  border-bottom: 1px solid #ebedf0;
}

.m-profile__auth-upload-row {
  display: flex;
  gap: 12px;
  padding: 16px;
}

.m-profile__auth-upload-box {
  flex: 1;
  height: 100px;
  background: #F5FAFF;
  border: 1px dashed #C6E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.m-profile__auth-upload-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.m-profile__auth-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #ccc;
}

.m-profile__radio-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 14px;
}

.m-profile__radio-label {
  width: 85px;
  color: #646566;
  flex-shrink: 0;
}

.m-profile__validity-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.m-profile__validity-row .van-field {
  width: 100%;
}

.m-profile__validity-end-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.m-profile__validity-end-row .van-field {
  flex: 1;
}

.m-profile__longterm {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 10px;
  flex-shrink: 0;
  font-size: 14px;
  color: #323233;
}
</style>
